<template>
  <div class="employee-dashboard" :class="{ 'nav-collapsed': isNavCollapsed }">
    <Navigation @nav-toggle="handleNavToggle" />

    <!-- Main Content -->
    <div class="dashboard-content">
      <div class="page-container">
        <!-- Loading State -->
        <div v-if="loading" class="loading-container">
          <div class="spinner"></div>
          <p>Loading your dashboard...</p>
        </div>

        <!-- Error State -->
        <div v-else-if="loadError" class="error-container">
          <i class="bx bx-error-circle"></i>
          <h3>Unable to load dashboard</h3>
          <p>{{ loadError }}</p>
          <button class="retry-btn" @click="loadDashboard">
            <i class="bx bx-refresh"></i> Try again
          </button>
        </div>

        <template v-else-if="dashboardData">
          <!-- Modern page header -->
          <div class="modern-page-header">
            <div class="header-content">
              <div class="title-section">
                <h1 class="dashboard-title">Welcome back, {{ dashboardData.employee?.firstName || "Employee" }}!</h1>
                <p class="dashboard-subtitle">
                  <span class="dashboard-subline">
                    <i class="bx bx-calendar" aria-hidden="true"></i>
                    {{ getCurrentDate() }}
                  </span>
                  <span class="dashboard-subtle-sep" aria-hidden="true">·</span>
                  <span class="dashboard-subline">
                    <i class="bx bx-buildings" aria-hidden="true"></i>
                    {{ dashboardData.employee?.department || "—" }}
                  </span>
                </p>
              </div>
              <div class="header-actions">
                <span v-if="notificationUnreadCount > 0" class="notification-header-badge" title="Unread items on current page">
                  <i class="bx bx-bell"></i>
                  {{ notificationUnreadCount }}
                </span>
                <button class="refresh-btn" @click="refreshDashboard" :disabled="refreshing">
                  <i class="bx bx-refresh" :class="{ 'spin': refreshing }"></i>
                  {{ refreshing ? 'Refreshing...' : 'Refresh' }}
                </button>
                <button class="profile-btn" @click="gotTo('employee-profile')">
                  <i class="bx bx-user"></i>
                  My Profile
                </button>
              </div>
            </div>
        
          </div>

          <!-- Today's Status Card -->
          <div class="status-card">
            <div class="status-header">
              <div class="status-info">
                <h3 class="status-title">
                  <i class="bx bx-time-five"></i>
                  Today's Status - {{ dashboardData.todayStatus?.dayOfWeek || 'Today' }}
                </h3>
                <div class="status-badges">
                  <div v-if="isClockedIn" class="status-badge success">
                    <i class="bx bx-check-circle"></i>
                    <span>Clocked In: {{ realtimeClockStatus?.checkInTime || dashboardData.todayStatus?.attendance?.checkInTime || dashboardData.todayStatus?.checkInTime || '—' }}</span>
                  </div>
                  <div v-else class="status-badge warning">
                    <i class="bx bx-time"></i>
                    <span>Not Clocked In Yet</span>
                  </div>

                  <div v-if="isClockedOut" class="status-badge info">
                    <i class="bx bx-log-out-circle"></i>
                    <span>Clocked Out: {{ realtimeClockStatus?.checkOutTime || dashboardData.todayStatus?.attendance?.checkOutTime || dashboardData.todayStatus?.checkOutTime || '—' }}</span>
                  </div>
                </div>
              </div>
              <div class="status-actions">
                <button v-if="!isCurrentlyClockedIn"
                        @click="clockIn"
                        class="clock-btn clock-in"
                        :disabled="clockProcessing">
                  <i class="bx bx-log-in-circle"></i>
                  {{ clockProcessing ? 'Processing...' : 'Clock In' }}
                </button>
                <button v-else
                        @click="clockOut"
                        class="clock-btn clock-out"
                        :disabled="clockProcessing">
                  <i class="bx bx-log-out-circle"></i>
                  {{ clockProcessing ? 'Processing...' : 'Clock Out' }}
                </button>
              </div>
            </div>
          </div>

          <!-- Quick Stats Cards -->
          <div class="metrics-grid">
            <!-- Attendance Rate -->
            <div class="metric-card gradient-1" @click="gotTo('employee-attendance-records')">
              <div class="metric-icon">
                <i class="bx bx-calendar-check"></i>
              </div>
              <div class="metric-content">
                <p class="metric-label">Attendance Rate</p>
                <h2 class="metric-value">{{ (dashboardData.quickStats?.attendance?.rate ?? 0) }}%</h2>
                <div class="metric-footer">
                  <span class="metric-detail">
                    {{ dashboardData.quickStats?.attendance?.presentDays ?? 0 }}/{{ dashboardData.quickStats?.attendance?.workingDays ?? 0 }} days
                  </span>
                </div>
                <div class="metric-progress">
                  <div class="progress-bar" :style="{ width: (dashboardData.quickStats?.attendance?.rate ?? 0) + '%' }"></div>
                </div>
              </div>
            </div>

            <!-- Leave Balance -->
            <div class="metric-card gradient-2" @click="gotTo('employee-leave-apply')">
              <div class="metric-icon">
                <i class="bx bx-plane-takeoff"></i>
              </div>
              <div class="metric-content">
                <p class="metric-label">Leave Balance</p>
                <h2 class="metric-value">{{ getTotalLeaveBalance() }}</h2>
                <div class="metric-footer">
                  <span class="metric-detail">
                    {{ dashboardData.quickStats?.leave?.pendingRequests ?? 0 }} pending requests
                  </span>
                </div>
              </div>
            </div>

            <!-- Working Hours -->
            <div class="metric-card gradient-3" @click="gotTo('employee-attendance-records')">
              <div class="metric-icon">
                <i class="bx bx-time"></i>
              </div>
              <div class="metric-content">
                <p class="metric-label">Hours This Month</p>
                <h2 class="metric-value">{{ dashboardData.quickStats?.attendance?.totalHours ?? 0 }}h</h2>
                <div class="metric-footer">
                  <span class="metric-detail">
                    Avg: {{ dashboardData.quickStats?.attendance?.avgHoursPerDay ?? 0 }}h/day
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Notifications (leave decisions, portal messages from HR, documents, etc.) -->
          <div class="notifications-section">
            <div class="content-card notifications-card">
              <div class="card-header">
                <div class="header-title-wrapper">
                  <h4 class="card-title">
                    <i class="bx bx-bell"></i>
                    Notifications
                  </h4>
                  <span v-if="notificationsTotalCount > 0" class="notifications-count-chip">{{ notificationsTotalCount }}</span>
                </div>
                <div class="notifications-actions">
                  <button
                    type="button"
                    class="view-all-btn"
                    @click="showAllNotificationsModal = true"
                    :disabled="notifications.length === 0 && !notificationsLoading"
                    title="Show all notifications"
                  >
                    <i class="bx bx-list-ul"></i>
                    Show all notifications
                  </button>
                  <button type="button" class="view-all-btn" @click="loadNotifications" :disabled="notificationsLoading" title="Reload notifications">
                    <i class="bx bx-refresh" :class="{ spin: notificationsLoading }"></i>
                    {{ notificationsLoading ? 'Loading…' : 'Reload' }}
                  </button>
                </div>
              </div>
              <div class="card-body">
                <p v-if="notificationsError" class="notifications-inline-error">{{ notificationsError }}</p>
                <div v-if="notificationsLoading && notifications.length === 0" class="notifications-loading-hint">
                  <i class="bx bx-loader-alt bx-spin"></i>
                  Loading notifications…
                </div>
                <div v-else-if="!notificationsLoading && notifications.length === 0 && !notificationsError" class="empty-state">
                  <i class="bx bx-bell-off"></i>
                  <p>No notifications in the last 30 days</p>
                </div>
                <div v-else class="notification-groups-grid">
                  <button
                    v-for="group in primaryNotificationGroups"
                    :key="`card-${group.key}`"
                    type="button"
                    class="notification-group-tile"
                    @click="openNotificationGroupFromCard(group.key)"
                  >
                    <div class="notification-group-icon">
                      <i :class="getNotificationGroupIcon(group.key)"></i>
                    </div>
                    <div class="notification-group-content">
                      <h5 class="notification-group-title">{{ group.label }}</h5>
                      <p class="notification-group-subtitle">({{ getGroupNotificationCount(group.key) }} notifications)</p>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- All notifications grouped modal -->
          <div v-if="showAllNotificationsModal" class="notifications-modal-overlay" @click.self="showAllNotificationsModal = false">
            <div class="notifications-modal-card">
              <div class="notifications-modal-header">
                <h4>All Notifications</h4>
                <button type="button" class="notifications-modal-close" @click="showAllNotificationsModal = false">×</button>
              </div>
              <div class="notifications-modal-body">
                <div v-if="notifications.length === 0" class="empty-state">
                  <i class="bx bx-bell-off"></i>
                  <p>No notifications available</p>
                </div>
                <div v-else>
                  <div v-if="!selectedNotificationGroup" class="notification-groups-grid">
                    <button
                      v-for="group in primaryNotificationGroups"
                      :key="group.key"
                      type="button"
                      class="notification-group-tile"
                      @click="openNotificationGroupList(group.key)"
                    >
                      <div class="notification-group-icon">
                        <i :class="getNotificationGroupIcon(group.key)"></i>
                      </div>
                      <div class="notification-group-content">
                        <h5 class="notification-group-title">{{ group.label }}</h5>
                        <p class="notification-group-subtitle">View {{ group.label.toLowerCase() }}</p>
                      </div>
                      <span class="notification-group-count">{{ getGroupNotificationCount(group.key) }}</span>
                    </button>
                  </div>
                  <div v-else>
                    <div class="notification-group-list-header">
                      <button type="button" class="view-all-btn" @click="selectedNotificationGroup = null">
                        <i class="bx bx-chevron-left"></i> Back
                      </button>
                      <h5 class="notification-group-title mb-0">
                        {{ getNotificationGroupLabel(selectedNotificationGroup) }}
                        <span class="notification-group-count">{{ selectedGroupNotifications.length }}</span>
                      </h5>
                    </div>
                    <div v-if="selectedGroupNotifications.length === 0" class="empty-state">
                      <i class="bx bx-bell-off"></i>
                      <p>No {{ getNotificationGroupLabel(selectedNotificationGroup).toLowerCase() }} notifications.</p>
                    </div>
                    <div v-else class="list-items notification-list">
                      <button
                        v-for="n in selectedGroupNotifications"
                        :key="`sel-${selectedNotificationGroup}-${n.type}-${n.id}-${n.timestamp}`"
                        type="button"
                        class="list-item notification-item notification-item-btn"
                        @click="openNotificationItem(n)"
                      >
                        <div :class="getNotificationIconWrapClass(n.priority)">
                          <i :class="getNotificationIcon(n.type)"></i>
                        </div>
                        <div class="item-content">
                          <p class="notification-message">{{ n.message }}</p>
                          <p class="item-subtitle">
                            <i class="bx bx-time"></i>
                            {{ n.timeAgo || formatDateTime(n.timestamp) }}
                          </p>
                        </div>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Alerts Section -->
          <div v-if="(dashboardData.alerts?.expiringDocuments?.length ?? 0) > 0" class="alert-section">
            <div class="alert-card warning">
              <div class="alert-header">
                <i class="bx bx-error-circle"></i>
                <strong>Action Required!</strong>
              </div>
              <p class="alert-message">
                You have {{ (dashboardData.alerts?.expiringDocuments?.length ?? 0) }} document(s) expiring soon.
              </p>
              <ul class="alert-list">
                <li v-for="doc in (dashboardData.alerts?.expiringDocuments || [])" :key="doc.id">
                  <strong>{{ doc.name }}</strong> expires on {{ formatDate(doc.validTill) }}
                  ({{ doc.daysUntilExpiry }} days remaining)
                </li>
              </ul>
            </div>
          </div>

          <!-- Main Content Grid -->
          <div class="content-grid">
            <!-- Recent Attendance -->
            <div class="content-card">
              <div class="card-header">
                <div class="header-title-wrapper">
                  <h4 class="card-title">
                    <i class="bx bx-calendar-clock"></i>
                    Recent Attendance
                  </h4>
                </div>
                <button class="view-all-btn" @click="gotTo('employee-attendance-records')">
                  View All <i class="bx bx-chevron-right"></i>
                </button>
              </div>
              <div class="card-body">
                <div v-if="(dashboardData.recentActivity?.attendance?.length ?? 0) === 0" class="empty-state">
                  <i class="bx bx-calendar-x"></i>
                  <p>No recent attendance records</p>
                </div>
                <div v-else class="data-table">
                  <div class="table-responsive">
                    <table>
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Check In</th>
                          <th>Check Out</th>
                          <th>Hours</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="record in (dashboardData.recentActivity?.attendance || [])" :key="record.date">
                          <td>{{ formatDate(record.date) }}</td>
                          <td>
                            <span v-if="record.checkInTime" class="time-badge success">
                              <i class="bx bx-log-in"></i> {{ record.checkInTime }}
                            </span>
                            <span v-else class="text-muted">-</span>
                          </td>
                          <td>
                            <span v-if="record.checkOutTime" class="time-badge danger">
                              <i class="bx bx-log-out"></i> {{ record.checkOutTime }}
                            </span>
                            <span v-else class="text-muted">-</span>
                          </td>
                          <td>{{ record.hoursWorked || '0' }}h</td>
                          <td>
                            <span class="status-badge success">{{ record.status || 'Present' }}</span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            <!-- Recent Leave Requests -->
            <div class="content-card">
              <div class="card-header">
                <div class="header-title-wrapper">
                  <h4 class="card-title">
                    <i class="bx bx-plane-takeoff"></i>
                    Recent Leave Requests
                  </h4>
                </div>
                <button class="view-all-btn" @click="gotTo('employee-leave-history')">
                  View All <i class="bx bx-chevron-right"></i>
                </button>
              </div>
              <div class="card-body">
                <div v-if="(dashboardData.recentActivity?.leaves?.length ?? 0) === 0" class="empty-state">
                  <i class="bx bx-plane-off"></i>
                  <p>No recent leave requests</p>
                </div>
                <div v-else class="data-table">
                  <div class="table-responsive">
                    <table>
                      <thead>
                        <tr>
                          <th>Leave Type</th>
                          <th>Period</th>
                          <th>Days</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="leave in (dashboardData.recentActivity?.leaves || [])" :key="leave.id">
                          <td>{{ leave.leaveTypeName }}</td>
                          <td>
                            <div class="date-range">
                              <span>{{ formatDate(leave.startDate) }}</span>
                              <span class="separator">to</span>
                              <span>{{ formatDate(leave.endDate) }}</span>
                            </div>
                          </td>
                          <td>{{ leave.totalDays }}</td>
                          <td>
                            <span :class="getLeaveStatusClass(leave.status)">
                              {{ leave.status }}
                            </span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            <!-- Upcoming Holidays -->
            <div class="content-card">
              <div class="card-header">
                <div class="header-title-wrapper">
                  <h4 class="card-title">
                    <i class="bx bx-calendar-star"></i>
                    Upcoming Holidays
                  </h4>
                </div>
              </div>
              <div class="card-body">
                <div v-if="(dashboardData.upcoming?.holidays?.length ?? 0) === 0" class="empty-state">
                  <i class="bx bx-calendar"></i>
                  <p>No upcoming holidays</p>
                </div>
                <div v-else class="list-items">
                  <div v-for="holiday in (dashboardData.upcoming?.holidays || [])"
                       :key="holiday.id"
                       class="list-item">
                    <div class="item-icon holiday">
                      <i class="bx bx-calendar"></i>
                    </div>
                    <div class="item-content">
                      <h6 class="item-title">{{ holiday.name }}</h6>
                      <p class="item-subtitle">
                        <i class="bx bx-time"></i>
                        {{ formatDate(holiday.date) }}
                      </p>
                      <span class="item-badge">{{ holiday.type }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Announcements -->
            <div class="content-card">
              <div class="card-header">
                <div class="header-title-wrapper">
                  <h4 class="card-title">
                    <i class="bx bx-bullhorn"></i>
                    Latest Announcements
                  </h4>
                </div>
                <button class="view-all-btn" @click="gotTo('employee-announcements')">
                  View All <i class="bx bx-chevron-right"></i>
                </button>
              </div>
              <div class="card-body">
                <div v-if="(dashboardData.announcements?.length ?? 0) === 0" class="empty-state">
                  <i class="bx bx-bullhorn-outline"></i>
                  <p>No announcements</p>
                </div>
                <div v-else class="list-items">
                  <div v-for="announcement in (dashboardData.announcements || [])"
                       :key="announcement.id"
                       class="list-item">
                    <div :class="getAnnouncementIconClass(announcement.priority)">
                      <i class="bx bx-bullhorn"></i>
                    </div>
                    <div class="item-content">
                      <h6 class="item-title">{{ announcement.title }}</h6>
                      <p class="item-description">
                        {{ truncateText(announcement.content, 100) }}
                      </p>
                      <p class="item-subtitle">
                        <i class="bx bx-time"></i>
                        {{ formatDate(announcement.publish_date) }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Leave Balance Breakdown -->
          <div class="balance-section">
            <div class="section-header">
              <h3 class="section-title">
                <i class="bx bx-pie-chart-alt"></i>
                Leave Balance Breakdown
              </h3>
            </div>
            <div v-if="(dashboardData.quickStats?.leave?.balances?.length ?? 0) === 0" class="empty-state-large">
              <i class="bx bx-info-circle"></i>
              <p>No leave balance information available</p>
            </div>
            <div v-else class="balance-grid">
              <div v-for="balance in (dashboardData.quickStats?.leave?.balances || [])"
                   :key="balance.leaveTypeName"
                   class="balance-card">
                <div class="balance-header">
                  <h6 class="balance-type">{{ balance.leaveTypeName }}</h6>
                </div>
                <div class="balance-content">
                  <div class="balance-value">
                    <h3>{{ balance.remainingDays }}</h3>
                    <span class="balance-label">of {{ balance.totalDays }} days</span>
                  </div>
                  <div class="balance-progress">
                    <div class="progress-bar" :style="{ width: getLeavePercentage(balance) + '%' }"></div>
                  </div>
                  <div class="balance-info">
                    <span class="info-item">
                      <i class="bx bx-check-circle"></i>
                      Used: {{ balance.usedDays }} days
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <!-- Footer -->
          <footer class="modern-footer">
            <div class="footer-content">
              <div class="footer-left">
                <p>{{ new Date().getFullYear() }} © County Government of Homa Bay. All rights reserved.</p>

              </div>
              <div class="footer-right">
                <p class="powered-by">
                  Powered by Craft Silicon
                </p>
              </div>
            </div>
          </footer>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import Navigation from "@/components/Navigation.vue";
import { authUrl, execute } from "@/api";

export default {
  name: "EmployeeDashboard",
  components: { Navigation },

  data() {
    return {
      isNavCollapsed: false,
      loading: false,
      refreshing: false,
      loadError: null,
      dashboardData: null,
      clockProcessing: false,
      realtimeClockStatus: null,
      notifications: [],
      notificationsLoading: false,
      notificationsError: null,
      notificationUnreadCount: 0,
      notificationsTotalCount: 0,
      showAllNotificationsModal: false,
      selectedNotificationGroup: null
    };
  },

  mounted() {
    this.loadDashboard();
    this.loadRealtimeClockStatus();
    this.loadNotifications();
    this._attendanceRefreshTimer = setInterval(() => {
      if (document.visibilityState === 'visible') {
        this.loadDashboard();
        this.loadRealtimeClockStatus();
      }
    }, 90000);
    document.addEventListener('visibilitychange', this._onAttendanceVisibility);
  },

  beforeUnmount() {
    if (this._attendanceRefreshTimer) {
      clearInterval(this._attendanceRefreshTimer);
    }
    document.removeEventListener('visibilitychange', this._onAttendanceVisibility);
  },

  computed: {
    isClockedIn() {
      if (this.realtimeClockStatus) return !!this.realtimeClockStatus.isClockedIn;
      const t = this.dashboardData?.todayStatus || {};
      return !!(t.clockedIn || t?.attendance?.checkInTime || t.checkInTime);
    },
    isClockedOut() {
      if (this.realtimeClockStatus) return !!this.realtimeClockStatus.checkOutTime;
      const t = this.dashboardData?.todayStatus || {};
      return !!(t.clockedOut || t?.attendance?.checkOutTime || t.checkOutTime);
    },
    isCurrentlyClockedIn() {
      if (this.realtimeClockStatus) return !!this.realtimeClockStatus.isClockedIn;
      const t = this.dashboardData?.todayStatus || {};
      const inRaw = t?.attendance?.checkInTime || t.checkInTime || null;
      const outRaw = t?.attendance?.checkOutTime || t.checkOutTime || null;
      if (inRaw && !outRaw) return true;
      if (!inRaw) return false;
      if (!outRaw) return true;
      const parseTime = (val) => {
        const s = String(val || "").trim();
        const m = s.match(/(\d{1,2}):(\d{2})(?::(\d{2}))?/);
        if (!m) return null;
        return (Number(m[1]) * 3600) + (Number(m[2]) * 60) + Number(m[3] || 0);
      };
      const inSecs = parseTime(inRaw);
      const outSecs = parseTime(outRaw);
      if (inSecs == null || outSecs == null) return !!t.clockedIn && !t.clockedOut;
      return inSecs > outSecs;
    },
    groupedNotifications() {
      const groups = new Map();
      const order = ["announcement", "event", "meeting", "leave_request", "contact", "document", "imprest", "attendance"];
      for (const n of this.notifications || []) {
        const key = String(n?.type || "other").toLowerCase();
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(n);
      }
      const sortItems = (items) => [...items].sort((a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0));
      const sortedKeys = Array.from(groups.keys()).sort((a, b) => {
        const ai = order.indexOf(a);
        const bi = order.indexOf(b);
        if (ai === -1 && bi === -1) return a.localeCompare(b);
        if (ai === -1) return 1;
        if (bi === -1) return -1;
        return ai - bi;
      });
      return sortedKeys.map((key) => ({
        key,
        label: this.getNotificationGroupLabel(key),
        items: sortItems(groups.get(key))
      }));
    },
    primaryNotificationGroups() {
      return [
        { key: "announcement", label: "Announcements" },
        { key: "event", label: "Events" },
        { key: "meeting", label: "Meetings" }
      ];
    },
    selectedGroupNotifications() {
      const key = String(this.selectedNotificationGroup || "").toLowerCase();
      if (!key) return [];
      const group = this.groupedNotifications.find((g) => g.key === key);
      return group ? group.items : [];
    }
  },

  methods: {
    async loadRealtimeClockStatus() {
      try {
        const data = new FormData();
        data.append("function", "getEmployeeClockStatus");
        const res = await execute(data, authUrl);
        if (res?.data?.success && res?.data?.data?.clockStatus) {
          this.realtimeClockStatus = res.data.data.clockStatus;
        }
      } catch (_) {
        this.realtimeClockStatus = null;
      }
    },

    // Navigation toggle handler
    handleNavToggle(isCollapsed) {
      this.isNavCollapsed = isCollapsed;
    },

    async loadDashboard() {
      this.loading = true;
      this.loadError = null;

      try {
        const data = new FormData();
        data.append("function", "getEmployeeDashboard");

        const res = await execute(data, authUrl);

        if (res && res.data && res.data.success) {
          this.dashboardData = res.data.data;
        } else {
          throw new Error(res.data?.message || 'Failed to load dashboard');
        }
      } catch (error) {
        console.error("Failed to load dashboard:", error);
        this.loadError = error?.message || 'Unable to load dashboard. Please try again.';
        this.dashboardData = null;
      } finally {
        this.loading = false;
      }
    },

    _onAttendanceVisibility() {
      if (document.visibilityState === 'visible') {
        this.refreshDashboard();
      }
    },

    async refreshDashboard() {
      this.refreshing = true;
      try {
        await Promise.all([
          this.loadDashboard(),
          this.loadRealtimeClockStatus(),
          this.loadNotifications()
        ]);
      } finally {
        this.refreshing = false;
      }
    },

    async loadNotifications() {
      this.notificationsLoading = true;
      this.notificationsError = null;
      try {
        const fd = new FormData();
        fd.append("function", "getEmployeeNotifications");
        fd.append("limit", "25");
        fd.append("page", "1");
        const res = await execute(fd, authUrl);
        if (res?.data?.success && res?.data?.data) {
          const d = res.data.data;
          const list = d.notifications;
          this.notifications = Array.isArray(list) ? list : [];
          this.notificationUnreadCount = Number(d.unreadCount) || 0;
          this.notificationsTotalCount = Number(d.totalCount) || this.notifications.length;
        } else {
          throw new Error(res?.data?.message || "Failed to load notifications");
        }
      } catch (e) {
        console.error("Failed to load notifications:", e);
        this.notificationsError = e?.message || "Could not load notifications.";
        this.notifications = [];
        this.notificationUnreadCount = 0;
        this.notificationsTotalCount = 0;
      } finally {
        this.notificationsLoading = false;
      }
    },

    notificationIsUnread(n) {
      return n && (n.read === 0 || n.read === "0" || n.read === false);
    },

    getNotificationIcon(type) {
      const t = String(type || "").toLowerCase();
      if (t === "leave_request") return "bx bx-plane-takeoff";
      if (t === "contact") return "bx bx-message-dots";
      if (t === "document") return "bx bx-file-blank";
      if (t === "imprest") return "bx bx-wallet";
      if (t === "attendance") return "bx bx-time-five";
      return "bx bx-bell";
    },
    getNotificationGroupLabel(type) {
      const t = String(type || "").toLowerCase();
      if (t === "announcement") return "Announcements";
      if (t === "event") return "Events";
      if (t === "meeting") return "Meetings";
      if (t === "leave_request") return "Leave Requests";
      if (t === "contact") return "Messages";
      if (t === "document") return "Documents";
      if (t === "imprest") return "Imprest";
      if (t === "attendance") return "Attendance";
      return "Other Notifications";
    },
    getNotificationGroupIcon(type) {
      const t = String(type || "").toLowerCase();
      if (t === "announcement") return "bx bx-megaphone";
      if (t === "event") return "bx bx-calendar-event";
      if (t === "meeting") return "bx bx-group";
      if (t === "leave_request") return "bx bx-plane-takeoff";
      if (t === "contact") return "bx bx-message-dots";
      if (t === "document") return "bx bx-file";
      if (t === "imprest") return "bx bx-wallet";
      if (t === "attendance") return "bx bx-time-five";
      return "bx bx-bell";
    },
    getGroupNotificationCount(type) {
      const t = String(type || "").toLowerCase();
      const group = this.groupedNotifications.find((g) => g.key === t);
      return group ? group.items.length : 0;
    },
    openNotificationGroupList(type) {
      this.selectedNotificationGroup = String(type || "").toLowerCase();
    },
    openNotificationGroupFromCard(type) {
      this.showAllNotificationsModal = true;
      this.selectedNotificationGroup = String(type || "").toLowerCase();
    },
    openNotificationItem(notification) {
      const t = String(notification?.type || "").toLowerCase();
      this.showAllNotificationsModal = false;
      this.selectedNotificationGroup = null;
      if (t === "event") {
        this.gotTo("hr-my-events");
        return;
      }
      if (t === "announcement") {
        this.gotTo("employee-announcements");
        return;
      }
      if (t === "meeting") {
        this.gotTo("employee-my-meetings");
        return;
      }
      // fallback to relevant self-service pages for other groups
      if (t === "leave_request") {
        this.gotTo("employee-leave-history");
        return;
      }
      if (t === "document") {
        this.gotTo("employee-documents-view");
        return;
      }
      if (t === "attendance") {
        this.gotTo("employee-attendance-records");
        return;
      }
      this.gotTo("employee-dashboard");
    },

    getNotificationIconWrapClass(priority) {
      const p = String(priority || "").toLowerCase();
      if (p === "danger") return "item-icon danger";
      if (p === "success") return "item-icon success";
      if (p === "warning") return "item-icon warning";
      return "item-icon info";
    },

    formatDateTime(date) {
      if (!date) return "N/A";
      const d = new Date(date);
      if (Number.isNaN(d.getTime())) return String(date);
      return d.toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      });
    },

    async clockIn() {
      await this.performClockAction("IN");
    },

    async clockOut() {
      await this.performClockAction("OUT");
    },

    async performClockAction(action) {
      if (this.clockProcessing) return;
      this.clockProcessing = true;
      try {
        const intendedAction = action;
        const location = await this.getCurrentLocationForClock();
        const data = new FormData();
        data.append("function", "employeeClockAction");
        data.append("action", action);
        data.append("latitude", String(location.latitude));
        data.append("longitude", String(location.longitude));
        data.append("address", location.address);
        data.append("notes", "");

        const res = await execute(data, authUrl);
        if (res?.data?.success) {
          if (this.$toast) this.$toast.success(res.data.message || (action === "IN" ? "Clock in successful" : "Clock out successful"));
          else alert(res?.data?.message || (action === "IN" ? "Clock in successful" : "Clock out successful"));
          await this.loadDashboard();
          await this.loadRealtimeClockStatus();
        } else {
          // Reconcile with latest dashboard state to avoid false negatives.
          await this.loadDashboard();
          await this.loadRealtimeClockStatus();
          const status = this.dashboardData?.todayStatus || {};
          const actionActuallyApplied =
            (intendedAction === "IN" && !!(this.realtimeClockStatus?.isClockedIn || status.clockedIn || status?.attendance?.checkInTime || status.checkInTime)) ||
            (intendedAction === "OUT" && !!(this.realtimeClockStatus?.checkOutTime || status.clockedOut || status?.attendance?.checkOutTime || status.checkOutTime));
          if (actionActuallyApplied) {
            if (this.$toast) this.$toast.success(res?.data?.message || "Attendance recorded.");
            else alert(res?.data?.message || "Attendance recorded.");
          } else {
            throw new Error(res?.data?.message || "Failed to record attendance.");
          }
        }
      } catch (error) {
        const msg = error?.message || "Could not complete clock action.";
        const locationIssue =
          msg.toLowerCase().includes("location") ||
          msg.toLowerCase().includes("geolocation") ||
          msg.toLowerCase().includes("permission");
        if (locationIssue) {
          const fallbackMsg = "Location is required to clock in/out from dashboard. Redirecting to Attendance Clock page.";
          if (this.$toast) this.$toast.warning(fallbackMsg);
          else alert(fallbackMsg);
          this.gotTo("employee-attendance-clock");
        } else {
          // Request may fail client-side after backend already saved; reconcile before showing error.
          await this.loadDashboard();
          await this.loadRealtimeClockStatus();
          const status = this.dashboardData?.todayStatus || {};
          const actionActuallyApplied =
            (action === "IN" && !!(this.realtimeClockStatus?.isClockedIn || status.clockedIn || status?.attendance?.checkInTime || status.checkInTime)) ||
            (action === "OUT" && !!(this.realtimeClockStatus?.checkOutTime || status.clockedOut || status?.attendance?.checkOutTime || status.checkOutTime));

          if (actionActuallyApplied) {
            const okMsg = action === "IN" ? "Clock in successful." : "Clock out successful.";
            if (this.$toast) this.$toast.success(okMsg);
            else alert(okMsg);
          } else {
            if (this.$toast) this.$toast.error(msg);
            else alert(msg);
          }
        }
      } finally {
        this.clockProcessing = false;
      }
    },

    getCurrentLocationForClock() {
      return new Promise((resolve, reject) => {
        if (!navigator.geolocation) {
          reject(new Error("Location is not supported by your browser."));
          return;
        }
        navigator.geolocation.getCurrentPosition(
          async (pos) => {
            const latitude = pos.coords.latitude;
            const longitude = pos.coords.longitude;
            let address = `Location: ${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
            try {
              const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1`, { headers: { Accept: "application/json" } });
              if (res.ok) {
                const data = await res.json();
                const a = data.address || {};
                const parts = [a.road, a.suburb, a.city, (a.county && !a.city) ? a.county : null, a.country].filter(Boolean);
                if (parts.length) address = parts.join(", ");
                else if (data.display_name) address = data.display_name;
              }
            } catch (_) {}
            resolve({ latitude, longitude, address });
          },
          (err) => {
            if (err.code === 1) reject(new Error("Location access denied. Please enable location permissions."));
            else if (err.code === 2) reject(new Error("Location unavailable. Please try again."));
            else if (err.code === 3) reject(new Error("Location request timed out. Please try again."));
            else reject(new Error("Failed to get location."));
          },
          { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
        );
      });
    },

    getCurrentDate() {
      const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
      return new Date().toLocaleDateString('en-US', options);
    },

    formatDate(date) {
      if (!date) return 'N/A';
      const d = new Date(date);
      const options = { year: 'numeric', month: 'short', day: 'numeric' };
      return d.toLocaleDateString('en-US', options);
    },

    formatCurrency(amount) {
      if (!amount) return '0.00';
      return parseFloat(amount).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },

    getTotalLeaveBalance() {
      const balances = this.dashboardData?.quickStats?.leave?.balances;
      if (!Array.isArray(balances)) return 0;
      return balances.reduce((total, balance) => total + (parseFloat(balance.remainingDays) || 0), 0);
    },

    getTotalAlerts() {
      if (!this.dashboardData?.quickStats) return 0;
      const q = this.dashboardData.quickStats;
      return (
          (q.leave?.pendingRequests ?? 0) +
          (q.imprest?.pendingRequests ?? 0) +
          (q.documents?.expiringSoon ?? 0)
      );
    },

    getLeavePercentage(balance) {
      if (!balance.totalDays || balance.totalDays == 0) return 0;
      return Math.round((balance.remainingDays / balance.totalDays) * 100);
    },

    getLeaveStatusClass(status) {
      const statusLower = status?.toLowerCase();
      if (statusLower === 'approved') return 'status-badge success';
      if (statusLower === 'rejected') return 'status-badge danger';
      if (statusLower === 'pending') return 'status-badge warning';
      return 'status-badge neutral';
    },

    getAnnouncementIconClass(priority) {
      const priorityLower = priority?.toLowerCase();
      if (priorityLower === 'high' || priorityLower === 'urgent') {
        return 'item-icon danger';
      }
      if (priorityLower === 'medium') {
        return 'item-icon warning';
      }
      return 'item-icon info';
    },

    truncateText(text, maxLength) {
      if (!text) return '';
      // Strip HTML tags
      const stripped = text.replace(/<[^>]*>/g, '');
      if (stripped.length <= maxLength) return stripped;
      return stripped.substring(0, maxLength) + '...';
    },

    gotTo(route) {
      const path = typeof route === 'string' && route.startsWith('/') ? route : '/' + (route || 'employee-dashboard');
      sessionStorage.setItem("selectedSub", route || 'employee-dashboard');
      sessionStorage.setItem("currentPage", route || 'employee-dashboard');
      this.$router.push(path);
    }
  }
};
</script>

<style scoped>
/* ===== Modern Employee Dashboard Styles ===== */
.employee-dashboard {
  min-height: 100vh;
  background: linear-gradient(160deg, #f1f5f9 0%, #e8f4fc 40%, #f8fafc 100%);
}

.employee-dashboard.nav-collapsed .dashboard-content {
  margin-left: 70px;
}

.dashboard-content {
  margin-left: 280px;
  padding: 20px;
  transition: margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.page-container {
  max-width: 1600px;
  margin: 0 auto;
}

/* Loading State */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  color: #64748b;
}

.spinner {
  width: 50px;
  height: 50px;
  border: 4px solid rgba(79, 70, 229, 0.1);
  border-top-color: #059669;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin-bottom: 20px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Error State */
.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 50vh;
  padding: 40px;
  text-align: center;
  color: #64748b;
}
.error-container i {
  font-size: 64px;
  color: #ef4444;
  margin-bottom: 20px;
}
.error-container h3 {
  font-size: 22px;
  color: #1e293b;
  margin: 0 0 12px 0;
}
.error-container p {
  margin: 0 0 24px 0;
  max-width: 400px;
}
.retry-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: linear-gradient(135deg, #059669 0%, #16a34a 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}
.retry-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(5, 150, 105, 0.3);
}

/* Modern Page Header */
.modern-page-header {
  background: linear-gradient(180deg, #ffffff 0%, #fafcff 100%);
  border-radius: 16px;
  padding: 26px 28px;
  box-shadow:
    0 1px 2px rgba(15, 23, 42, 0.04),
    0 12px 40px rgba(13, 79, 115, 0.08);
  margin-bottom: 28px;
  border: 1px solid rgba(226, 232, 240, 0.95);
  position: relative;
  overflow: hidden;
}

.modern-page-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #0d9488 0%, #059669 42%, #34d399 100%);
  border-radius: 16px 16px 0 0;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 20px;
  margin-bottom: 15px;
}

.title-section {
  flex: 1;
  min-width: 300px;
}

.dashboard-title {
  font-size: clamp(1.5rem, 2.5vw, 2rem);
  font-weight: 800;
  color: #0f172a;
  margin: 0;
  line-height: 1.25;
  letter-spacing: -0.02em;
}

.dashboard-subtitle {
  color: #64748b;
  font-size: 15px;
  margin: 12px 0 0 0;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.dashboard-subline {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.dashboard-subline .bx {
  font-size: 18px;
  color: #0d9488;
}

.dashboard-subtle-sep {
  color: #cbd5e1;
  font-weight: 300;
  user-select: none;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.refresh-btn,
.profile-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  border: none;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.refresh-btn {
  background: linear-gradient(135deg, #059669 0%, #16a34a 100%);
  color: white;
}

.refresh-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 8px 24px rgba(5, 150, 105, 0.28);
}

.refresh-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.refresh-btn i.spin {
  animation: spin 1s linear infinite;
}

.profile-btn {
  background: #f1f5f9;
  color: #475569;
  border: 1px solid #e2e8f0;
}

.profile-btn:hover {
  background: #e2e8f0;
  color: #059669;
}

.breadcrumb-section {
  padding-top: 15px;
  border-top: 1px solid #e2e8f0;
}

.breadcrumb {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #475569;
  font-size: 14px;
  font-weight: 500;
  flex-wrap: wrap;
}

.breadcrumb-item {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #475569;
  cursor: pointer;
  transition: color 0.2s;
}

.breadcrumb-item:hover {
  color: #059669;
}

.breadcrumb-current {
  color: #94a3b8;
  font-weight: 600;
}

.breadcrumb-separator {
  color: #cbd5e1;
}

/* Status Card */
.status-card {
  background: linear-gradient(135deg, #dbeafe 0%, #eff6ff 100%);
  border-radius: 20px;
  padding: 25px 30px;
  margin-bottom: 30px;
  border: 1px solid #bfdbfe;
  box-shadow: 0 4px 20px rgba(59, 130, 246, 0.1);
}

.status-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 20px;
}

.status-info {
  flex: 1;
  min-width: 300px;
}

.status-title {
  font-size: 20px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 15px 0;
  display: flex;
  align-items: center;
  gap: 10px;
}

.status-title i {
  color: #3b82f6;
  font-size: 24px;
}

.status-badges {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
}

.status-badge.success {
  background: rgba(16, 185, 129, 0.15);
  color: #065f46;
}

.status-badge.warning {
  background: rgba(245, 158, 11, 0.15);
  color: #92400e;
}

.status-badge.info {
  background: rgba(59, 130, 246, 0.15);
  color: #1e40af;
}

.status-badge i {
  font-size: 18px;
}

.status-actions {
  display: flex;
  align-items: center;
}

.clock-btn {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 28px;
  border: none;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.clock-btn i {
  font-size: 20px;
}

.clock-in {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  box-shadow: 0 4px 15px rgba(16, 185, 129, 0.3);
}

.clock-in:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 25px rgba(16, 185, 129, 0.4);
}

.clock-out {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: white;
  box-shadow: 0 4px 15px rgba(239, 68, 68, 0.3);
}

.clock-out:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 25px rgba(239, 68, 68, 0.4);
}

.completed {
  background: #94a3b8;
  color: white;
  cursor: not-allowed;
  opacity: 0.8;
}

/* Metrics Grid */
.metrics-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 260px), 1fr));
  gap: 22px;
  margin-bottom: 28px;
}

.metric-card {
  position: relative;
  border-radius: 16px;
  padding: 22px 22px 22px 24px;
  overflow: hidden;
  border: 1px solid rgba(226, 232, 240, 0.95);
  box-shadow:
    0 1px 2px rgba(15, 23, 42, 0.04),
    0 10px 32px rgba(15, 23, 42, 0.06);
  transition: transform 0.22s ease, box-shadow 0.22s ease;
  background: #ffffff;
  min-height: 176px;
  cursor: pointer;
}

.metric-card:hover {
  transform: translateY(-3px);
  box-shadow:
    0 14px 40px rgba(15, 23, 42, 0.09);
}

.gradient-1 {
  background: linear-gradient(145deg, #eff6ff 0%, #ffffff 72%);
  border-left: 4px solid #3b82f6;
}

.gradient-2 {
  background: linear-gradient(145deg, #ecfdf5 0%, #ffffff 72%);
  border-left: 4px solid #10b981;
}

.gradient-3 {
  background: linear-gradient(145deg, #fffbeb 0%, #ffffff 72%);
  border-left: 4px solid #f59e0b;
}

.gradient-4 {
  background: linear-gradient(135deg, #fee2e2 0%, #fef2f2 100%);
  border-left: 4px solid #ef4444;
}

.metric-icon {
  position: absolute;
  top: 25px;
  right: 25px;
  width: 60px;
  height: 60px;
  background: white;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.metric-icon i {
  font-size: 28px;
}

.gradient-1 .metric-icon i { color: #3b82f6; }
.gradient-2 .metric-icon i { color: #10b981; }
.gradient-3 .metric-icon i { color: #f59e0b; }
.gradient-4 .metric-icon i { color: #ef4444; }

.metric-content {
  padding-right: 70px;
}

.metric-label {
  color: #64748b;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.metric-value {
  color: #1e293b;
  font-size: 36px;
  font-weight: 800;
  margin: 0 0 10px 0;
  line-height: 1;
}

.metric-footer {
  margin-top: 12px;
}

.metric-detail {
  color: #64748b;
  font-size: 13px;
  font-weight: 500;
}

.metric-progress {
  margin-top: 15px;
  height: 6px;
  background: rgba(0, 0, 0, 0.1);
  border-radius: 10px;
  overflow: hidden;
}

.metric-progress .progress-bar {
  height: 100%;
  background: linear-gradient(90deg, #059669 0%, #16a34a 100%);
  border-radius: 10px;
  transition: width 0.3s ease;
}

/* Alert Section */
.alert-section {
  margin-bottom: 30px;
}

.alert-card {
  background: #ffffff;
  border-radius: 16px;
  padding: 20px 25px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
}

.alert-card.warning {
  background: linear-gradient(135deg, #fef3c7 0%, #fefce8 100%);
  border-left: 4px solid #f59e0b;
}

.alert-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
  font-size: 16px;
  font-weight: 700;
  color: #92400e;
}

.alert-header i {
  font-size: 24px;
}

.alert-message {
  color: #78350f;
  margin: 0 0 15px 0;
  font-size: 14px;
}

.alert-list {
  list-style: none;
  padding: 0;
  margin: 0;
  color: #78350f;
}

.alert-list li {
  padding: 8px 0;
  border-bottom: 1px solid rgba(251, 191, 36, 0.2);
  font-size: 14px;
}

.alert-list li:last-child {
  border-bottom: none;
}

/* Notifications (API: getEmployeeNotifications) */
.notifications-section {
  margin-bottom: 28px;
}

.notifications-card {
  border-radius: 16px !important;
  overflow: hidden;
  border: 1px solid rgba(226, 232, 240, 0.95) !important;
  box-shadow:
    0 1px 2px rgba(15, 23, 42, 0.04),
    0 12px 36px rgba(13, 79, 115, 0.06) !important;
}

.notifications-count-chip {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  margin-left: 8px;
  font-size: 12px;
  font-weight: 700;
  color: #475569;
  background: #f1f5f9;
  border-radius: 999px;
}

.notification-header-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  margin-right: 8px;
  font-size: 13px;
  font-weight: 700;
  color: #065f46;
  background: rgba(16, 185, 129, 0.15);
  border-radius: 999px;
}

.notification-header-badge i {
  font-size: 18px;
}

.notifications-inline-error {
  color: #dc2626;
  font-size: 14px;
  margin: 0 0 12px 0;
}

.notifications-loading-hint {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 28px 20px;
  color: #64748b;
  font-size: 14px;
}

.notifications-loading-hint i {
  font-size: 22px;
  color: #059669;
}

.notification-message {
  margin: 0 0 6px 0;
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
  line-height: 1.45;
}

.notification-details {
  font-size: 13px;
  color: #64748b;
  margin: 0 0 8px 0;
  line-height: 1.45;
}

.notification-unread-dot {
  margin-left: 10px;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.03em;
  color: #fff;
  background: linear-gradient(135deg, #059669 0%, #10b981 100%);
  border-radius: 6px;
}

.view-all-btn i.spin {
  animation: spin 1s linear infinite;
}

.notifications-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}

.notifications-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3000;
  padding: 16px;
}

.notifications-modal-card {
  width: min(960px, 100%);
  max-height: 88vh;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 20px 45px rgba(15, 23, 42, 0.2);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.notifications-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 16px;
  border-bottom: 1px solid #e2e8f0;
}

.notifications-modal-header h4 {
  margin: 0;
  font-size: 18px;
  color: #0f172a;
}

.notifications-modal-close {
  border: none;
  background: transparent;
  font-size: 24px;
  line-height: 1;
  color: #64748b;
  cursor: pointer;
}

.notifications-modal-body {
  padding: 12px 16px 16px;
  overflow-y: auto;
}

.notification-group-section + .notification-group-section {
  margin-top: 16px;
}

.notification-group-title {
  margin: 0 0 10px 0;
  font-size: 15px;
  color: #0f172a;
  display: flex;
  align-items: center;
  gap: 8px;
}

.notification-group-count {
  background: #e2e8f0;
  color: #334155;
  border-radius: 999px;
  padding: 2px 8px;
  font-size: 12px;
  font-weight: 600;
}

.notification-groups-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 12px;
}

.notification-group-tile {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #fff;
  padding: 12px;
  display: flex;
  align-items: center;
  gap: 10px;
  text-align: left;
  transition: all 0.2s ease;
  cursor: pointer;
}

.notification-group-tile:hover {
  border-color: #059669;
  box-shadow: 0 6px 16px rgba(2, 132, 199, 0.12);
  transform: translateY(-1px);
}

.notification-group-icon {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: rgba(5, 150, 105, 0.1);
  color: #059669;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 19px;
}

.notification-group-content {
  flex: 1;
  min-width: 0;
}

.notification-group-subtitle {
  margin: 4px 0 0 0;
  color: #64748b;
  font-size: 12px;
}

.notification-group-list-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  gap: 10px;
}

.notification-item-btn {
  width: 100%;
  border: 1px solid transparent;
  background: #fff;
  text-align: left;
  cursor: pointer;
}

.item-icon.success {
  background: rgba(16, 185, 129, 0.12);
  color: #059669;
}

/* Content Grid — use horizontal space on wide screens without narrowing the page */
.content-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 340px), 1fr));
  gap: 22px;
  margin-bottom: 36px;
}

.content-card {
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid rgba(226, 232, 240, 0.95);
  box-shadow:
    0 1px 2px rgba(15, 23, 42, 0.04),
    0 10px 32px rgba(15, 23, 42, 0.06);
  overflow: hidden;
  transition: transform 0.22s ease, box-shadow 0.22s ease;
}

.content-card:hover {
  transform: translateY(-2px);
  box-shadow:
    0 14px 42px rgba(15, 23, 42, 0.09);
}

.card-header {
  background: linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
  padding: 18px 22px;
  border-bottom: 1px solid #eef2f7;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-title-wrapper {
  display: flex;
  align-items: center;
  gap: 12px;
}

.card-title {
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.card-title i {
  font-size: 20px;
  color: #0d9488;
}

.view-all-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: none;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  color: #64748b;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.view-all-btn:hover {
  background: #f1f5f9;
  color: #059669;
  border-color: #cbd5e1;
}

.view-all-btn i {
  font-size: 16px;
}

.card-body {
  padding: 20px;
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: #94a3b8;
  text-align: center;
}

.empty-state i {
  font-size: 48px;
  margin-bottom: 15px;
  color: #cbd5e1;
}

.empty-state p {
  margin: 0;
  font-size: 15px;
  color: #64748b;
}

.empty-state-large {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #94a3b8;
  text-align: center;
}

.empty-state-large i {
  font-size: 64px;
  margin-bottom: 20px;
  color: #cbd5e1;
}

.empty-state-large p {
  margin: 0;
  font-size: 16px;
  color: #64748b;
}

/* Data Table */
.data-table {
  overflow-x: auto;
}

.table-responsive {
  min-width: 100%;
}

table {
  width: 100%;
  border-collapse: collapse;
}

thead {
  background: #f8fafc;
}

th {
  padding: 12px 15px;
  text-align: left;
  font-weight: 600;
  color: #475569;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 2px solid #e2e8f0;
}

td {
  padding: 15px;
  border-bottom: 1px solid #e2e8f0;
  color: #1e293b;
  font-size: 14px;
}

tbody tr:hover {
  background: #f8fafc;
}

.text-muted {
  color: #94a3b8;
}

.time-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
}

.time-badge.success {
  background: rgba(16, 185, 129, 0.1);
  color: #065f46;
}

.time-badge.danger {
  background: rgba(239, 68, 68, 0.1);
  color: #991b1b;
}

.time-badge i {
  font-size: 14px;
}

.date-range {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 13px;
}

.date-range .separator {
  color: #94a3b8;
  font-size: 11px;
}

.status-badge {
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
}

.status-badge.success {
  background: rgba(16, 185, 129, 0.1);
  color: #065f46;
}

.status-badge.danger {
  background: rgba(239, 68, 68, 0.1);
  color: #991b1b;
}

.status-badge.warning {
  background: rgba(245, 158, 11, 0.1);
  color: #92400e;
}

.status-badge.neutral {
  background: rgba(100, 116, 139, 0.1);
  color: #475569;
}

/* List Items */
.list-items {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.list-item {
  display: flex;
  gap: 15px;
  padding: 15px;
  border-radius: 12px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  transition: all 0.2s;
}

.list-item:hover {
  background: #f1f5f9;
  border-color: #cbd5e1;
}

.item-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.item-icon i {
  font-size: 24px;
}

.item-icon.holiday {
  background: rgba(245, 158, 11, 0.1);
  color: #f59e0b;
}

.item-icon.info {
  background: rgba(59, 130, 246, 0.1);
  color: #3b82f6;
}

.item-icon.warning {
  background: rgba(245, 158, 11, 0.1);
  color: #f59e0b;
}

.item-icon.danger {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
}

.item-content {
  flex: 1;
}

.item-title {
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 6px 0;
}

.item-subtitle {
  font-size: 13px;
  color: #64748b;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 6px;
}

.item-subtitle i {
  font-size: 14px;
}

.item-description {
  font-size: 14px;
  color: #475569;
  margin: 6px 0;
  line-height: 1.5;
}

.item-badge {
  display: inline-block;
  padding: 4px 10px;
  background: rgba(79, 70, 229, 0.1);
  color: #4338ca;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  margin-top: 6px;
}

/* Balance Section */
.balance-section {
  background: linear-gradient(180deg, #ffffff 0%, #fafcff 100%);
  border-radius: 16px;
  padding: 26px;
  box-shadow:
    0 1px 2px rgba(15, 23, 42, 0.04),
    0 12px 36px rgba(13, 79, 115, 0.06);
  margin-bottom: 36px;
  border: 1px solid rgba(226, 232, 240, 0.95);
}

.section-header {
  margin-bottom: 25px;
}

.section-title {
  font-size: 20px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
}

.section-title i {
  color: #0d9488;
  font-size: 24px;
}

.balance-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
}

.balance-card {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  overflow: hidden;
  transition: all 0.3s ease;
}

.balance-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
}

.balance-header {
  background: linear-gradient(135deg, #059669 0%, #16a34a 100%);
  padding: 15px 20px;
}

.balance-type {
  color: white;
  font-size: 15px;
  font-weight: 700;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.balance-content {
  padding: 20px;
}

.balance-value {
  text-align: center;
  margin-bottom: 15px;
}

.balance-value h3 {
  font-size: 36px;
  font-weight: 800;
  color: #059669;
  margin: 0;
}

.balance-label {
  display: block;
  font-size: 13px;
  color: #64748b;
  margin-top: 6px;
}

.balance-progress {
  height: 8px;
  background: rgba(79, 70, 229, 0.1);
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 12px;
}

.balance-progress .progress-bar {
  height: 100%;
  background: linear-gradient(90deg, #059669 0%, #16a34a 100%);
  border-radius: 10px;
  transition: width 0.3s ease;
}

.balance-info {
  display: flex;
  justify-content: center;
}

.info-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #64748b;
}

.info-item i {
  color: #10b981;
  font-size: 16px;
}

/* Payslip Card */
.payslip-card {
  background: linear-gradient(135deg, #d1fae5 0%, #f0fdf4 100%);
  border-radius: 20px;
  padding: 30px;
  margin-bottom: 40px;
  border: 1px solid #a7f3d0;
  box-shadow: 0 4px 20px rgba(16, 185, 129, 0.1);
}

.payslip-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 20px;
}

.payslip-info {
  flex: 1;
  min-width: 300px;
}

.payslip-title {
  font-size: 22px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 10px 0;
  display: flex;
  align-items: center;
  gap: 10px;
}

.payslip-title i {
  color: #10b981;
  font-size: 28px;
}

.payslip-period {
  color: #065f46;
  font-size: 14px;
  margin: 0 0 15px 0;
}

.payslip-amount {
  font-size: 42px;
  font-weight: 800;
  color: #10b981;
  margin: 0;
}

.payslip-actions {
  display: flex;
  align-items: center;
}

.payslip-btn {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 28px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 4px 15px rgba(16, 185, 129, 0.3);
}

.payslip-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 25px rgba(16, 185, 129, 0.4);
}

.payslip-btn i {
  font-size: 20px;
}

/* Footer */
.modern-footer {
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  border-top: 1px solid #e8ecf1;
  padding: 22px 0 26px;
  margin-top: 36px;
  border-radius: 16px 16px 0 0;
  box-shadow: 0 -4px 24px rgba(15, 23, 42, 0.05);
}

.footer-content {
  width: 100%;
  max-width: 1600px;
  margin: 0 auto;
  padding: 0 8px;
  box-sizing: border-box;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 15px;
}

.footer-left p,
.footer-right p {
  color: #64748b;
  font-size: 14px;
  margin: 0;
}

.system-info {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  font-size: 13px;
}

.system-info i {
  color: #059669;
}

.powered-by {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #94a3b8;
  font-style: italic;
}

.powered-by i {
  color: #059669;
}

/* Responsive Design */
@media (max-width: 1024px) {
  .content-grid {
    grid-template-columns: 1fr;
  }
  
  .dashboard-content {
    margin-left: 70px;
  }
  
  .employee-dashboard.nav-collapsed .dashboard-content {
    margin-left: 70px;
  }
}

@media (max-width: 768px) {
  .dashboard-content {
    margin-left: 0;
    padding: 15px;
  }
  
  .employee-dashboard.nav-collapsed .dashboard-content {
    margin-left: 0;
  }

  .modern-page-header {
    padding: 20px;
  }

  .header-content {
    flex-direction: column;
    align-items: stretch;
  }

  .dashboard-title {
    font-size: 24px;
  }

  .metrics-grid {
    grid-template-columns: 1fr;
  }

  .status-header {
    flex-direction: column;
    align-items: stretch;
  }

  .status-actions {
    justify-content: center;
    margin-top: 15px;
  }

  .balance-grid {
    grid-template-columns: 1fr;
  }

  .payslip-content {
    flex-direction: column;
    text-align: center;
  }

  .payslip-actions {
    justify-content: center;
    width: 100%;
  }

  .payslip-btn {
    width: 100%;
    justify-content: center;
  }

  .footer-content {
    flex-direction: column;
    text-align: center;
  }
}

@media (max-width: 480px) {
  .dashboard-title {
    font-size: 20px;
  }

  .metric-content {
    padding-right: 60px;
  }

  .metric-icon {
    width: 50px;
    height: 50px;
  }

  .metric-icon i {
    font-size: 24px;
  }

  .metric-value {
    font-size: 28px;
  }

  .status-title {
    font-size: 18px;
  }

  .clock-btn {
    width: 100%;
    justify-content: center;
  }

  .payslip-amount {
    font-size: 32px;
  }

  table {
    font-size: 13px;
  }

  th, td {
    padding: 10px;
  }
}

/* Print Styles */
@media print {
  .header-actions,
  .status-actions,
  .view-all-btn,
  .modern-footer,
  .refresh-btn,
  .profile-btn,
  .clock-btn,
  .payslip-btn {
    display: none !important;
  }
  
  .dashboard-content {
    margin-left: 0 !important;
    padding: 0 !important;
  }
  
  .metric-card,
  .content-card,
  .balance-card {
    break-inside: avoid;
    box-shadow: none !important;
    border: 1px solid #ddd !important;
  }
}
</style>